import { useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Modal, Button, Checkbox, Input, Select } from '@/components/ui'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday, utcDateOnly } from '@/lib/date'
import type { MaintenanceSchedule, User } from '@/types'

interface ScheduleFormData {
  title: string
  description: string
  frequency: string
  next_due_at: string
  active: boolean
  assigned_to_id: string
}

interface ScheduleModalProps {
  schedule: MaintenanceSchedule | null
  users: User[]
  onClose: () => void
  onSave: (data: ScheduleFormData) => Promise<void>
  isSaving: boolean
}

export function ScheduleModal({ schedule, users, onClose, onSave, isSaving }: ScheduleModalProps) {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const { timezone } = useLocalizationConfig()

  const calcNextDueAt = useCallback((frequency: string) => {
    // B-020: anchor on the account's local calendar day, not the browser's —
    // `new Date()` + toISOString().split('T')[0] reads the UTC day, which is
    // wrong for zones ahead of UTC (see localToday's doc comment).
    const today = new Date(localToday(timezone))
    switch (frequency) {
      case 'daily': today.setDate(today.getDate() + 1); break
      case 'weekly': today.setDate(today.getDate() + 7); break
      case 'biweekly': today.setDate(today.getDate() + 14); break
      case 'monthly': today.setMonth(today.getMonth() + 1); break
      case 'quarterly': today.setMonth(today.getMonth() + 3); break
      case 'semi_annual': today.setMonth(today.getMonth() + 6); break
      case 'annual': today.setFullYear(today.getFullYear() + 1); break
    }
    return utcDateOnly(today)
  }, [timezone])

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ScheduleFormData>({
    defaultValues: {
      title: '',
      description: '',
      frequency: 'monthly',
      next_due_at: '',
      active: true,
      assigned_to_id: '',
    },
  })

  useEffect(() => {
    if (schedule) {
      reset({
        title: schedule.title,
        description: schedule.description || '',
        frequency: schedule.frequency,
        next_due_at: schedule.next_due_at
          ? utcDateOnly(new Date(schedule.next_due_at))
          : '',
        active: schedule.active,
        assigned_to_id: schedule.assigned_to_id ? String(schedule.assigned_to_id) : '',
      })
    } else {
      reset({
        title: '',
        description: '',
        frequency: 'monthly',
        next_due_at: calcNextDueAt('monthly'),
        active: true,
        assigned_to_id: '',
      })
    }
  }, [schedule, reset, calcNextDueAt])

  const frequency = watch('frequency')
  useEffect(() => {
    if (!schedule) {
      setValue('next_due_at', calcNextDueAt(frequency))
    }
  }, [frequency, schedule, setValue, calcNextDueAt])

  const frequencyOptions = [
    { value: 'daily', label: t('frequencies.daily') },
    { value: 'weekly', label: t('frequencies.weekly') },
    { value: 'biweekly', label: t('frequencies.biweekly') },
    { value: 'monthly', label: t('frequencies.monthly') },
    { value: 'quarterly', label: t('frequencies.quarterly') },
    { value: 'semi_annual', label: t('frequencies.semi_annual') },
    { value: 'annual', label: t('frequencies.annual') },
  ]

  const userOptions = [
    { value: '', label: t('form.no_assignee') },
    ...users.map((u) => ({ value: String(u.id), label: u.full_name })),
  ]

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={schedule ? t('maintenance_schedules.edit') : t('maintenance_schedules.add')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSave)}
            loading={isSaving}
          >
            {tc('actions.save')}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        <Input
          label={t('labels.title')}
          error={errors.title?.message}
          {...register('title', { required: tc('errors.required') })}
        />
        <Input
          label={t('labels.description')}
          {...register('description')}
        />
        <Select
          label={t('labels.frequency')}
          options={frequencyOptions}
          {...register('frequency', { required: tc('errors.required') })}
        />
        <Input
          label={t('labels.next_due_at')}
          type="date"
          {...register('next_due_at')}
        />
        <Select
          label={t('labels.assigned_to')}
          options={userOptions}
          {...register('assigned_to_id')}
        />
        <Checkbox size="sm" label={t('labels.active')} {...register('active')} />
      </form>
    </Modal>
  )
}
